import * as React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import { Navigate } from 'react-router-dom'
import VendorsPageTitle from 'vendors/components/VendorsPageTitle'
import VendorWizardUserBasicInfoForm from 'vendors/vendorWizard/userBasicInfoStep/VendorWizardUserBasicInfoForm'
import { useVendorWizard } from 'vendors/vendorWizard/VendorWizardContext'
import { useGetMe, useUpdateUserDetails } from 'vendors/vendorWizard/userBasicInfoStep/queries/useUserQuery'
import CircularProgress from '@mui/material/CircularProgress'
import LoadingButton from 'proposals/components/LoadingButton'
import { useTranslation } from 'react-i18next'

type VendorUserInputErrors = {
  firstname: string
  lastname: string
  [key: string]: string
}

const VendorWizardUserBasicInfo: React.FC<{ baseUrl: string }> = ({ baseUrl }) => {
  const { vendorUser, setVendorUser, initVendorUser } = useVendorWizard()
  const { t } = useTranslation()

  const { isLoading, isError, data: userData } = useGetMe()

  // Handle onSuccess behavior with useEffect (TanStack Query v5 migration)
  React.useEffect(() => {
    if (userData) {
      initVendorUser(userData)
    }
  }, [userData, initVendorUser])

  const {
    mutate: updateDetails,
    isPending: isUpdateLoading,
    isError: isUpdateError,
    isSuccess: isUpdateSuccess
  } = useUpdateUserDetails()

  const [vendorUserInputErrors, setVendorUserInputErrors] = React.useState<VendorUserInputErrors>({
    firstname: '',
    lastname: ''
  })

  const handleVendorFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setVendorUser({ ...vendorUser, [name]: value })

    if (vendorUserInputErrors[name] !== '') {
      setVendorUserInputErrors((oldState) => ({ ...oldState, [name]: '' }))
    }
  }

  const handleBlurValidation = (event: React.FocusEvent<HTMLInputElement>) => {
    const { value, name } = event.target
    const errorMessage = value === '' ? t('required-field') : ''
    setVendorUserInputErrors((oldState) => ({ ...oldState, [name]: errorMessage }))
  }

  const onNextClick = () => {
    const payload = {
      firstName: vendorUser.firstname,
      lastName: vendorUser.lastname,
      phone: vendorUser.phone,
      //30.5.2023 - 10583 GLOBALIZATION - hardcoded language
      language: 'fi'
    }
    updateDetails(payload)
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isError || isUpdateError) {
    return <Navigate to="/error" replace />
  }

  if (isUpdateSuccess) {
    return <Navigate to={`${baseUrl}/company`} replace />
  }

  return (
    <Box sx={{ px: 2, pb: 2 }}>
      <VendorsPageTitle>{t('check-your-information')}</VendorsPageTitle>
      <Box sx={{ mt: 3 }}>
        <VendorWizardUserBasicInfoForm
          values={vendorUser}
          errors={vendorUserInputErrors}
          onBlur={handleBlurValidation}
          onChange={handleVendorFieldChange}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, alignItems: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          {t('read')}{' '}
          <Link href="/privacy-policy" target="_blank" rel="noreferrer noopener">
            {t('privacy-policy')}
          </Link>{' '}
          {t('of-use-of-your-information')}
        </Typography>
        <LoadingButton
          disabled={!vendorUser.firstname || !vendorUser.lastname}
          onClick={onNextClick}
          isLoading={isUpdateLoading}
        >
          {t('next')}
        </LoadingButton>
      </Box>
    </Box>
  )
}

export default VendorWizardUserBasicInfo
